import { Dayjs } from 'dayjs'

import { getOnlyDate } from '~/astra'
import { OptimusSchemas } from '~/clients/optimus-client'
import { getPatientAgeGroup } from '~/store/utils/patientHelpers'
import { AgeGroup, Location, ScheduledSurgery, SchedulingSurgery, UnScheduledSurgery } from '~/storm'
import { getBookingIdOrId, getKnifeTime, getSurgeryRoomDuration, getTheaterRoom } from '~/utils/dips'

type Nullable<T> = { [K in keyof T]: T[K] | null | undefined }

function hasRequiredPropertiesForEvaluation(surgery: Nullable<OptimusSchemas['ScheduledDipsSurgery']>): surgery is OptimusSchemas['ScheduledDipsSurgery'] {
    return (
        typeof surgery.surgery_type_id === 'number' &&
        typeof surgery.room_code === 'string' &&
        surgery.room_code.length > 0 &&
        typeof surgery.planned_theatre_in_time === 'string' &&
        surgery.planned_theatre_in_time.length > 0
    )
}

export function mapDipsScheduledSurgeriesToDTO(
    scheduledSurgeries: (ScheduledSurgery | SchedulingSurgery)[],
    ageGroups: AgeGroup[],
    use_surgery_duration_model = false
): OptimusSchemas['ScheduledDipsSurgery'][] {
    return scheduledSurgeries
        .map((surgery): Nullable<OptimusSchemas['ScheduledDipsSurgery']> => {
            const patient = surgery.patient
            const ageGroupCode = patient ? getPatientAgeGroup(patient, ageGroups)?.age_group_code : undefined
            const estimatedKnifeTime = use_surgery_duration_model ? getKnifeTime(surgery) : null

            return {
                id: getBookingIdOrId(surgery),
                planned_duration: getSurgeryRoomDuration(surgery).toISOString(),
                surgery_type_id: surgery.surgeryType?.id,
                planned_theatre_in_time: surgery.plannedTheatreInTime ? getOnlyDate(surgery.plannedTheatreInTime) : undefined,
                room_code: getTheaterRoom(surgery.surgeryResources)?.shortName,
                age_group_code: ageGroupCode,
                estimated_knife_time: estimatedKnifeTime?.toISOString(),
            }
        })
        .filter(hasRequiredPropertiesForEvaluation)
}

export function mapDipsUnscheduledSurgeriesToDTO(
    unscheduledSurgeries: UnScheduledSurgery[],
    date: Dayjs,
    location: Location,
    ageGroups: AgeGroup[],
    use_surgery_duration_model = false
): OptimusSchemas['ScheduledDipsSurgery'][] {
    return unscheduledSurgeries
        .map((surgery): Nullable<OptimusSchemas['ScheduledDipsSurgery']> => {
            const estimatedKnifeTime = use_surgery_duration_model ? getKnifeTime(surgery) : null

            return {
                id: getBookingIdOrId(surgery),
                planned_duration: getSurgeryRoomDuration(surgery).toISOString(),
                surgery_type_id: surgery.surgeryType.id,
                planned_theatre_in_time: getOnlyDate(date),
                room_code: location.room_code,
                age_group_code: getPatientAgeGroup(surgery.patient, ageGroups)?.age_group_code,
                estimated_knife_time: estimatedKnifeTime?.toISOString(),
            }
        })
        .filter(hasRequiredPropertiesForEvaluation)
}
